import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {findRateByDate} from "crqlar-react-common/dist/utils/rate.util";
import {useBookingEngineConfigs} from "crqlar-react-common/dist/utils/hooks/bookingEngine/useBookingEngineConfigs";
import {useRates} from "crqlar-react-common/dist/utils/hooks/rates/useRates";
import {useAllTaxRates} from "crqlar-react-common/dist/utils/hooks/rates/useTaxRates";
import {useEffect, useRef} from "react";
import {withNoShowFee} from "../folioCharge.util";
import {rateVat} from "./serviceAutofill";
import {FolioSession} from "./useFolioSession";

interface Options {
    session: FolioSession
    reservation?: Reservation
    service?: Service
    /** Only the folio the dialog was opened with (the Charge flow's) gets these defaults. */
    folioId?: string
}

/** The booking engine's no-show fee for the reservation: per person, or the configured total. */
const useNoShowFee = (reservation?: Reservation, service?: Service): number | undefined => {
    const billingConfig = useBookingEngineConfigs(service?.sectorIds)?.[0]?.billingConfig;
    if (reservation?.status !== ReservationStatus.NO_SHOW) return undefined;

    const guests = reservation.guestCounts?.reduce((sum, count) => sum + (count.count ?? 0), 0) ?? 0;
    if (guests < (billingConfig?.minNumberOfPeopleForCharge as number)) return 0;
    return billingConfig?.chargePerPersonAmount && guests
        ? billingConfig.chargePerPersonAmount * guests
        : billingConfig?.totalChargeAmount;
};

/**
 * What the charge dialog puts on the reservation's line of its folio - all into the draft, which is
 * saved before anything is charged: the service's name, market code and id on a single unnamed line;
 * the rate's VAT, once per line, never over a choice staff made; and on a no-show the fee (with the
 * no-show service), once per folio and fee.
 */
export const useFolioLineDefaults = ({session, reservation, service, folioId}: Options) => {
    const organization = useOrganization();
    const taxRates = useAllTaxRates();
    const {folio, draft, linesEditable, services} = session;
    const firstLine = draft.lines[0];
    // Only on the reservation's own folio: a package's folio starts with the package's line, never the member's
    const applies = !!folio?.id && folio.id === folioId && folio.reservationId === reservation?.id && linesEditable && draft.ready;

    // The raw rate document carries the VAT; only for a concrete service - a VAT is never invented
    const serviceRateIds = (service?.rateIds ?? []) as string[];
    const rawServiceRates = useRates(serviceRateIds);
    const targetRawRate = serviceRateIds.length
        ? findRateByDate(rawServiceRates, reservation?.expectedStartDate || new Date())
        : undefined;
    const vatDefaulted = useRef(new Set<string>());

    const noShowFee = useNoShowFee(reservation, service);
    const noShowService = services.find(s => s.noShowService && s.sectorIds?.includes(reservation?.sectorId || ''));
    const noShowSaved = useRef<string>();

    useEffect(() => {
        if (!applies || !service || draft.lines.length !== 1 || !firstLine || firstLine.item.description) return;
        draft.update(firstLine.key, {
            description: service.name ?? '',
            serviceInventoryCode: service.marketCode ?? '',
            serviceId: service.id ?? firstLine.item.serviceId,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applies, service?.id, draft.lines.length, firstLine?.item.description]);

    useEffect(() => {
        if (!applies || !taxRates.length || reservation?.status === ReservationStatus.NO_SHOW) return;
        const id = firstLine?.item.id;
        if (!id || vatDefaulted.current.has(id)) return;
        // A VAT already there (saved or defaulted) is never touched again, so a cleared one stays cleared
        if (firstLine.item.taxRateId) {
            vatDefaulted.current.add(id);
            return;
        }
        const vat = rateVat(undefined, targetRawRate, taxRates);
        if (vat) {
            vatDefaulted.current.add(id);
            draft.update(firstLine.key, vat);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applies, targetRawRate?.taxRateId, targetRawRate?.taxRateSnap?.id, firstLine?.item.id, firstLine?.item.taxRateId, taxRates.length]);

    useEffect(() => {
        if (!applies || !noShowFee || noShowFee <= 0 || !firstLine?.item.id || firstLine.item.folioId !== folio?.id) return;
        const key = `${folio?.id}:${noShowFee}:${noShowService?.id ?? ''}`;
        if (noShowSaved.current === key) return;

        noShowSaved.current = key;
        const withFee = withNoShowFee(firstLine.item, noShowFee, organization?.currencyCode || 'EUR', noShowService);
        if (withFee) {
            const {price, description, serviceInventoryCode, serviceId} = withFee;
            draft.update(firstLine.key, {price, description, serviceInventoryCode, serviceId});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applies, firstLine?.item.id, noShowFee, noShowService?.id]);
};
